/**
 * 消費税率（10%）
 */
export const TAX_RATE = 0.1

/**
 * 税額を計算する
 * @param amount - 税抜金額
 * @returns 税額（小数点以下切り捨て）
 */
export const calculateTax = (amount: number): number => {
  return Math.floor(amount * TAX_RATE)
}

/**
 * 税込金額を計算する
 * @param amount - 税抜金額
 * @returns 税込金額（小数点以下切り捨て）
 */
export const calculateTaxIncluded = (amount: number): number => {
  return Math.floor(amount * (1 + TAX_RATE))
}

/**
 * 税込金額から税抜金額を逆算する
 * @param taxIncluded - 税込金額
 * @returns 税抜金額（calculateTax/calculateTaxIncludedと整合性のある値）
 */
export const deriveBaseFee = (taxIncluded: number): number => {
  const tax = Math.floor((taxIncluded / (1 + TAX_RATE)) * TAX_RATE)
  return taxIncluded - tax
}

/**
 * 金額を日本円形式でフォーマット
 * @param value - フォーマットする値（文字列、数値、null、undefined）
 * @returns フォーマットされた金額文字列（例: "¥1,000"）、値がない場合は"未設定"
 */
export const formatPrice = (value?: string | number | null): string => {
  if (value === undefined || value === null) {
    return '未設定'
  }
  const numericValue = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(numericValue)) {
    return value.toString()
  }
  return `¥${numericValue.toLocaleString('ja-JP')}`
}

/**
 * 金額を日本円形式でフォーマット（値がない場合はダッシュ表示）
 * @param amount - フォーマットする金額（数値、null）
 * @returns フォーマットされた金額文字列（例: "¥1,000"）、値がない場合は"¥ -"
 */
export const formatAmount = (amount: number | null): string => {
  if (amount === null) return '¥ -'
  return `¥${amount.toLocaleString('ja-JP')}`
}

/**
 * 税抜き金額から税込み金額を計算（null/undefined対応）
 * @param price - 税抜金額（null/undefined可）
 * @returns 税込金額、入力がnull/undefinedの場合はnull
 */
export const calculateTaxIncludedOrNull = (
  price: number | undefined | null,
): number | null => {
  if (price === undefined || price === null) return null
  return calculateTaxIncluded(price)
}

/**
 * 税抜き金額から税額を計算（null/undefined対応）
 * @param price - 税抜金額（null/undefined可）
 * @returns 税額、入力がnull/undefinedの場合はnull
 */
export const calculateTaxOrNull = (
  price: number | undefined | null,
): number | null => {
  if (price === undefined || price === null) return null
  return calculateTax(price)
}

/**
 * 税抜き金額と税額を詳細フォーマットで表示
 * @param price - 税抜金額（null/undefined可）
 * @param tax - 税額（null可）
 * @returns フォーマットされた詳細文字列（例: "( ¥1,000 + 税¥100 )"）
 */
export const formatPriceDetail = (
  price: number | undefined | null,
  tax: number | null,
): string => {
  if (price === undefined || price === null || tax === null) {
    return '( ¥- + 税¥- )'
  }
  return `( ¥${price.toLocaleString('ja-JP')} + 税¥${tax.toLocaleString('ja-JP')} )`
}

/**
 * 金額を「〇〇円」形式でフォーマット
 * @param value - フォーマットする値（文字列、数値、null、undefined）
 * @returns フォーマットされた金額文字列（例: "1,000円"）、値がない場合は"-"
 */
export const formatYen = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === '') {
    return '-'
  }
  const numericValue = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(numericValue)) {
    return '-'
  }
  return `${numericValue.toLocaleString('ja-JP')}円`
}

/**
 * 税込金額を「〇〇円」形式でフォーマット（基本料金 + 税額を合算）
 * @param baseFee - 基本料金（文字列、数値、null、undefined）
 * @param tax - 税額（文字列、数値、null、undefined）
 * @returns フォーマットされた税込金額文字列（例: "1,100円"）、両方とも値がない場合は"-"
 */
export const formatYenWithTax = (
  baseFee?: string | number | null,
  tax?: string | number | null,
): string => {
  const base =
    baseFee === undefined || baseFee === null || baseFee === ''
      ? 0
      : typeof baseFee === 'string'
        ? Number(baseFee)
        : baseFee
  const taxAmount =
    tax === undefined || tax === null || tax === ''
      ? 0
      : typeof tax === 'string'
        ? Number(tax)
        : tax

  if (Number.isNaN(base) || Number.isNaN(taxAmount)) {
    return '-'
  }

  const total = base + taxAmount
  if (total === 0 && !baseFee && !tax) {
    return '-'
  }
  return `${total.toLocaleString('ja-JP')}円`
}
